/**
 * useMemberPhotos — Loads cached profile photos for roster members.
 *
 * Fetches photos from /api/members/:id/photo and caches them as
 * object URLs in a Map<memberId, blobUrl>. Failed/missing photos
 * are silently skipped (the UI falls back to initials).
 */

import { useState, useEffect, useRef } from 'react';

export function useMemberPhotos(memberIds: number[]): Map<number, string> {
  const [photos, setPhotos] = useState<Map<number, string>>(new Map());
  const loadedRef = useRef<Set<number>>(new Set());
  const urlsRef = useRef<Map<number, string>>(new Map());

  useEffect(() => {
    if (memberIds.length === 0) return;

    const controller = new AbortController();
    const newIds = memberIds.filter((id) => !loadedRef.current.has(id));

    if (newIds.length === 0) return;

    // Mark as loading to prevent re-fetching
    for (const id of newIds) {
      loadedRef.current.add(id);
    }

    // Fetch photos in parallel (max 10 concurrent to avoid overwhelming the server)
    const batchSize = 10;
    let batchIndex = 0;

    async function fetchBatch() {
      while (batchIndex < newIds.length && !controller.signal.aborted) {
        const batch = newIds.slice(batchIndex, batchIndex + batchSize);
        batchIndex += batchSize;

        const results = await Promise.allSettled(
          batch.map(async (id) => {
            const res = await fetch(`/api/members/${id}/photo`, {
              credentials: 'include',
              signal: controller.signal,
            });
            if (!res.ok) return null;
            const blob = await res.blob();
            if (blob.size === 0) return null;
            return { id, url: URL.createObjectURL(blob) };
          }),
        );

        let updated = false;
        for (const result of results) {
          if (result.status === 'fulfilled' && result.value) {
            urlsRef.current.set(result.value.id, result.value.url);
            updated = true;
          }
        }

        if (updated && !controller.signal.aborted) {
          setPhotos(new Map(urlsRef.current));
        }
      }
    }

    fetchBatch().catch(() => {
      /* best-effort */
    });

    return () => {
      controller.abort();
    };
  }, [memberIds]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    const urls = urlsRef.current;
    return () => {
      for (const url of urls.values()) {
        URL.revokeObjectURL(url);
      }
    };
  }, []);

  return photos;
}
